import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    computed,
    CUSTOM_ELEMENTS_SCHEMA,
    effect,
    ElementRef,
    HostListener,
    inject,
    input,
    OnDestroy,
    signal,
    viewChild
} from '@angular/core';
import {WjFlexGrid, WjGridModule} from '@mescius/wijmo.angular2.grid';
import {CellRange, CellRangeEventArgs, ColumnGroup, FlexGrid} from '@mescius/wijmo.grid';
import {WjFlexGridFilter, WjGridFilterModule} from '@mescius/wijmo.angular2.grid.filter';
import {WjGridColumnChooser} from '../../lib/wjgrid-columchooser';
import {WjFlexGridSearch, WjGridSearchModule} from '@mescius/wijmo.angular2.grid.search';
import {AppFormat} from '../../lib/format';
import {WjGridChangeTracker} from '../../lib/wjgrid-change-tracker';
import {AccountGridRowCtxMenuService} from './account-grid-row-ctxmenu.service';
import {AccountGridContainerComponent, AccountGridCtxMenuService, AccountMenu} from './account-grid-ctxmenu.service';
import {UndoStack} from '@mescius/wijmo.undo';
import {PortfolioSegmentDataContext} from '../../data/portfolio-segment-data-context.service';
import {FormsModule} from '@angular/forms';
import {MwcCheckboxControlValueAccessorDirective} from '../../directives/mwc-checkbox-control-value-accessor.directive';
import {WjGridHeatmapCol} from '../../lib/wjgrid-heatmapcol';
import {ColorMap} from '../../lib/color-map';
import {Stdlib} from '../../lib/stdlib';
import {VisibilityObserverDirective} from '../../directives/visibility-observer.directive';
import {
    AccountData,
    AccountDataStatusEnum,
    AccountDataUpdateLog,
    PortfolioPeriodEnum
} from '../../../api-client-kt/src';
import {KeyValueAndParamsService} from '../../service/key-value-and-params.service';
import {AccountDataWithChecks, CheckSeverity, CheckUtils} from '../../data/app-data-checks';
import {IntervalWorker} from '../../lib/interval-worker';
import {ActivatedRoute, Router} from '@angular/router';
import {FlexGridXlsxConverter, XlsxFormatItemEventArgs} from '@mescius/wijmo.grid.xlsx';
import {Alerts, DialogButton} from '../../lib/alerts';
import {AccountGridService} from './account-grid-helper';
import {ColumnFilter, FilterType, FlexGridFilter, Operator} from '@mescius/wijmo.grid.filter';
import {AppGridViewService, GridLayoutState} from '../../service/app-grid-view.service';
import {Wijmo, WjGridUtils} from '../../lib/wjgrid-utils';
import {DataDictionaryService} from '../../data/data-dictionary';
import {PortfolioDataContext} from '../../data/portfolio-data-context.service';
import {NgDynamicComponentsService} from '../../service/ng-dynamic-components.service';
import {AccountGridCellRenderer} from './account-grid-cell-renderer';
import {AccessControlService} from '../../security/access-control.service';
import {AppConstants} from '../../app.constants';
import {StringUtils} from '../../lib/strings';
import {AngularUtils} from '../../lib/angular';
import {WjCoreModule} from '@mescius/wijmo.angular2.core';
import {Globalize} from '@mescius/wijmo';
import {
    AccountCommentsPaneComponent,
    AccountFieldComments
} from '../../components/account-comments-pane/account-comments-pane.component';
import {AppSessionStorage} from '../../lib/app-localstorage';
import {HtmlSplitter} from '../../lib/html-splitter';
import {ChildApiService} from '../../data/portfolio-context-resolver.service';
import {AgoraFetchDialogComponent} from './agora-fetch-dialog';
import {range} from 'es-toolkit';

const NO_ERROR_FILTER_VALUE = '(nothing)';

type AccountGridFilterConditionType = 'equals' | 'range';

export interface AccountGridFilterCondition<T = any> {
    column: string;
    type: AccountGridFilterConditionType;
    value1: T | undefined;
    value2?: T | undefined;
}

@Component({
    selector: 'app-account-grid',
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        WjCoreModule,
        WjGridModule,
        WjGridFilterModule,
        WjGridSearchModule,
        FormsModule,
        MwcCheckboxControlValueAccessorDirective,
        VisibilityObserverDirective,
        AccountCommentsPaneComponent
    ],
    templateUrl: './account-grid.component.html',
    styleUrl: './account-grid.component.scss'
})
export class AccountGridComponent implements AccountGridContainerComponent, AfterViewInit, OnDestroy {

    // read the URL query parameter 'errId' from the activated route to automatically apply the error filter
    protected readonly sessionFilterId = input<string | undefined>(undefined, { alias: 'filterId' });
    protected readonly segmentId = input<string | undefined>(undefined, { alias: 'segmentId' });

    private readonly accountGridRowCtxMenuService = inject(AccountGridRowCtxMenuService);
    private readonly accountGridCtxMenuService = inject(AccountGridCtxMenuService);
    private readonly keyValueAndParamsService = inject(KeyValueAndParamsService);

    protected readonly pfData = inject(PortfolioDataContext);
    protected readonly pfSegData = inject(PortfolioSegmentDataContext);
    private readonly childApiService = inject(ChildApiService);

    private gridChangeTracker!: WjGridChangeTracker;
    private serverSideAccountChanges = new Map<string, AccountDataUpdateLog>();
    protected hasServerSideChanges = signal(false);

    protected grid = viewChild.required<WjFlexGrid>('grid');
    protected gridFilter = viewChild<WjFlexGridFilter>('gridFilter');
    protected gridSearch = viewChild<WjFlexGridSearch>('gridSearch');
    protected gridIsFiltered = signal(false);
    protected gridFilterTooltip = signal('Test');
    protected visibleRowCount = signal(0);
    protected totalRowCount = computed(() => this.pfSegData.getAccountData().length);

    protected gridFrozenColumnsActive = signal(false);

    private gridHeaderBindingName!: keyof AccountData;
    private gridHeaderBindingAlign: string | undefined;
    private gridHeaderBindingFormat!: string;

    private gridCellRenderer: AccountGridCellRenderer | undefined;

    protected sortingIsActive = signal(false);
    protected commentPaneVisible = signal(AppConstants.getAccountCommentsPaneVisible());

    // Segment ID for the current portfolio segment, as provided in the URL
    //public segmentId = model<string>('n/a');

    private gridMenu!: AccountMenu;
    protected undoStack!: UndoStack;

    protected canUndo = signal(false);
    protected canRedo = signal(false);

    protected colVisibleTiering = signal(AppGridViewService.getAccountColumnGroupState('TieringGroup'));
    protected colVisibleTracking = signal(AppGridViewService.getAccountColumnGroupState('TrackingGroup'));
    protected colVisibleOutput = signal(AppGridViewService.getAccountColumnGroupState('OutputGroup'));

    private columnGroupCollapseStatusChecker: IntervalWorker | undefined;

    viewShareId = input<string | undefined>(undefined);

    private readonly route = inject(ActivatedRoute);
    private readonly router = inject(Router);
    private readonly ngDynCompSvc = inject(NgDynamicComponentsService);
    private readonly accessControlService = inject(AccessControlService);

    protected readonly period = this.pfSegData.periodFilter;

    protected readonly Wijmo = Wijmo;
    protected readonly AppFormat = AppFormat;

    private splitter: HtmlSplitter | undefined;

    constructor(private el: ElementRef) {
        effect(() => WjGridUtils.connectGridWithSignalSource('Accounts', this.pfSegData.accountsCollectionView.value, this.grid, this.gridFilter));
        effect(() => this.adjustColumnGroupVisibilities());
        effect(() => this.monitorAccountChanges());
        effect(() => this.updateErrorFilterValueList());
        effect(() => this.updateFilterFromQueryParam());
        effect(() => {
            return Stdlib.let(this.pfSegData.activeSegment.value(), (segment) => {
                this.gridCellRenderer?.updateSegment(segment);
            });
        });
        effect(() => this.applyPeriodFilter(this.pfSegData.periodFilter()));
        effect(() => this.attachSortActiveStatusListener());

        this.prepareHeaderBinding('row');
        // this.serverSideAccountChanges.set('0a2a0002-99e2-16d3-8199-e26be12103f5', { accountId: '0a2a0002-99e2-16d3-8199-e26be12103f5'} as any);
    }

    @HostListener('document:mousemove', ['$event'])
    onMouseMove(event: MouseEvent): void {
        this.splitter?.onMouseMove(event);
    }

    @HostListener('document:mouseup')
    onMouseUp(): void {
        this.splitter?.onMouseUp();
    }

    protected onResizeCommentsPaneStart(event: MouseEvent) {
        this.splitter?.onResizeStart(event);
    }

    getGrid(): FlexGrid {
        return this.grid();
    }

    getGridFilter(): FlexGridFilter {
        return this.gridFilter()!;
    }

    getPfSegData(): PortfolioSegmentDataContext {
        return this.pfSegData;
    }

    getChangeTracker(): WjGridChangeTracker {
        return this.gridChangeTracker;
    }

    async ngAfterViewInit(): Promise<void> {
        const gutterElement = this.el.nativeElement.querySelector('.gutter-col');
        const commentsPaneElement = this.el.nativeElement.querySelector('.comments-pane');
        this.splitter = new HtmlSplitter(gutterElement, commentsPaneElement);
    }

    async ngOnDestroy(): Promise<void> {
        console.log('ngOnDestroy');
        this.gridCellRenderer?.unbindFromGrid();
        this.columnGroupCollapseStatusChecker?.stop();
    }

    private prepareHeaderBinding(binding: string): void {
        const dd = DataDictionaryService.getAccountDataAttributeRequired(binding);
        this.gridHeaderBindingName = dd.attribute as keyof AccountData;
        this.gridHeaderBindingAlign = dd.align;
        this.gridHeaderBindingFormat = dd.format ?? '';
    }

    async onGridInitialized(grid: WjFlexGrid, gridFilter: WjFlexGridFilter) {
        await this.pfSegData.waitForResourcesToLoad(this.segmentId());

        // we must wait for the segment data to be fully loaded, cause otherwise the KPIs are missing
        const columnGroups = this.getMatchingColumnConfiguration();
        await AccountGridService.configureGridColumns(grid, columnGroups, this.pfSegData);

        WjGridColumnChooser.addColumnChooser('accountGrid', grid);
        this.gridChangeTracker = WjGridChangeTracker.addChangeTracker(grid);
        this.gridChangeTracker.onCellChanged.subscribe(this.onGridDataChanged.bind(this));

        grid.refreshed.addHandler(() => this.visibleRowCount.set(grid.rows.length));

        this.accountGridRowCtxMenuService.createMenu(this);
        this.gridCellRenderer = new AccountGridCellRenderer(
            grid, this.gridHeaderBindingName, this.gridHeaderBindingFormat,
            this.pfSegData.activeSegment.value()!, this.pfData.portfolio.value(),
            this.gridChangeTracker, this.serverSideAccountChanges,
            this.ngDynCompSvc, this.accessControlService
        );
        WjGridHeatmapCol.addHeatmapColumn(grid, 'scoreAts', ColorMap.ZeroToOneHeatMap, true);
        WjGridUtils.registerSelectedCellValueSignal(grid);
        WjGridUtils.activateCopyUnformattedMode(grid);

        // grid menu initialization
        const hostEl = this.el.nativeElement.querySelector('.menu-host-no-dropdown-button');
        this.gridMenu = this.accountGridCtxMenuService.createMenu(hostEl, this);

        this.adjustColumnGroupVisibilities();

        this.undoStack = new UndoStack(grid.hostElement, {
            maxActions: 50,
            stateChanged: (s: UndoStack) => {
                this.canUndo.set(s.canUndo);
                this.canRedo.set(s.canRedo);
            }
        });
        this.gridChangeTracker.connectUndoStack(this.undoStack);
        this.addPollingColumnGroupCollapseStatusChecker(this.grid()!);

        gridFilter.filterApplied.addHandler((f,e)=> {
            // heuristic - we don't consider the app-global 'period' filter as relevant
            // so when the global filter is 'on', we check if more than 1 filter is active
            const minFilters = this.pfSegData.periodFilter() != null ? 1 : 0;
            this.gridIsFiltered.set(WjGridUtils.countActiveFilters(f) > minFilters);
            if (grid.collectionView) {
                grid.collectionView.filter = this.initCustomFilterFunction(grid, gridFilter);
            }
            this.prepareFilterTooltip(gridFilter);
        });
        await this.updateFilterFromQueryParam();
        this.prepareFilterTooltip(gridFilter);

        // set filter values to initial grid filter (period as loaded from portfolio and errors)
        this.updateErrorFilterValueList();

        // use the header that was last configured by the user, or the default one if none was configured yet
        grid.hostElement.addEventListener('keydown', this.customKeyHandler.bind(this, grid));
        this.applyHeaderBinding(AppConstants.getAccountGridHeaderBinding());

        setTimeout(async () => await this.openSharedViewIfNeeded(grid, gridFilter), 400);
    }

    private customKeyHandler(grid: WjFlexGrid, e: KeyboardEvent) {
        if (e.key === 'ArrowDown' && e.ctrlKey && e.shiftKey){
            grid.select(new CellRange(grid.rows.length - 1, grid.selection.col, grid.selection.row2, grid.selection.col2));
        } else if(e.key === 'ArrowUp' && e.ctrlKey && e.shiftKey){
            grid.select(new CellRange(0, grid.selection.col, grid.selection.row2, grid.selection.col2));
        } else if(e.key === 'ArrowDown' && e.ctrlKey){
            grid.select(grid.rows.length - 1, grid.selection.col);
        } else if(e.key === 'ArrowUp' && e.ctrlKey){
            grid.select(0, grid.selection.col);
        }
    }

    private getMatchingColumnConfiguration() {
        if (this.pfData.portfolio.value().phase === "Tiering") {
            return AccountGridService.getTieringViewConfiguration();
        } else {
            return AccountGridService.getStandardViewConfiguration();
        }
    }

    private attachSortActiveStatusListener() {
        const sortDescriptions = this.pfSegData.accountsCollectionView.value().sortDescriptions;
        sortDescriptions.collectionChanged.addHandler(() => {
            console.log(`!!!!! Sorting changed ${sortDescriptions.length}`)
            this.sortingIsActive.set(sortDescriptions.length > 0);
        });
    }

    private async updateFilterFromQueryParam() {
        const filterId = this.sessionFilterId();
        if (!this.grid()?.isInitialized) return;

        if (filterId) {
            WjGridUtils.clearGridFilter(this.gridFilter(), false);
            await this.router.navigate([], {
                relativeTo: this.route,
                queryParams: { 'filterId': undefined },
                queryParamsHandling: 'merge',
                replaceUrl: true
            });

            const filterStorageKey = `accountGridSessionFilter_${filterId}`;
            const filterConditions: AccountGridFilterCondition[] = JSON.parse(sessionStorage.getItem(filterStorageKey) ?? '[]');
            sessionStorage.removeItem(filterStorageKey);

            for (const condition of filterConditions) {
                const columnFilter = this.gridFilter()?.getColumnFilter(condition.column);
                if (columnFilter) {
                    if (condition.type === 'range') {
                        columnFilter.conditionFilter.condition1.operator = Operator.GE;
                        columnFilter.conditionFilter.condition1.value = condition.value1;
                        columnFilter.conditionFilter.condition2.operator = Operator.LT;
                        columnFilter.conditionFilter.condition2.value = condition.value2;
                    } else if (condition.type === 'equals') {
                        const key = condition.value1 as any;
                        if (key !== 'undefined') {
                            columnFilter.valueFilter.showValues = {[key]: true};
                        } else {
                            columnFilter.valueFilter.showValues = { '': true };
                        }
                    }
                }
            }
        }

        this.applyPeriodFilter(this.pfSegData.periodFilter());
    }

    static async applyFilterAndShow(router: Router, route: ActivatedRoute, ...filters: AccountGridFilterCondition[]) {
        const filterId = StringUtils.newUuid4();
        sessionStorage.setItem(`accountGridSessionFilter_${filterId}`, JSON.stringify(filters));

        // dynamically, from the current activated route snapshot, go back to the account details URL
        // by finding the segmentId parameter in the URL and navigating relative to that
        const resolvedUrl = AngularUtils.getResolvedUrl(route.snapshot);
        const segmentIdIndex = resolvedUrl.indexOf('segment') + 1;

        const commands = resolvedUrl.slice(0, segmentIdIndex + 1);
        commands.push('accounts');

        await router.navigate(commands, { relativeTo: null, queryParams: { filterId } });
    }

    /**
     * Updates the list of values for the grid's "error level" column filter.
     * It gets the account data's distinct error codes populates the filter with
     * unique error codes.
     */
    updateErrorFilterValueList() {
        // to get registered on the effect(), access the formula evaluation value
        this.pfSegData.ensureEffectOnFormulaEvaluation();

        // walk over ALL accounts (not only the ones filtered by this period)
        // to get the distinct error codes, even when users switch the period
        const accounts = this.pfSegData.getAccountData();
        const errorFilter = this.gridFilter()?.getColumnFilter('errorLevel');
        if (errorFilter) {
            errorFilter.filterType = FilterType.Value;
            const distinctErrorCodes = CheckUtils.getDistinctErrorCodes(accounts);
            if (distinctErrorCodes.indexOf("warning") >= 0) {
                console.error('warning is a reserved error code');
            }

            errorFilter.valueFilter.uniqueValues = [NO_ERROR_FILTER_VALUE, ...distinctErrorCodes];
        }
    }

    setHeaderBinding(binding: string): void {
        AppConstants.setAccountGridHeaderBinding(binding);
        this.applyHeaderBinding(binding);
    }

    private applyHeaderBinding(binding: string | null): void {
        if (!binding) return;

        this.prepareHeaderBinding(binding);

        Stdlib.let(this.gridCellRenderer, cr => {
            cr.gridHeaderBindingName = this.gridHeaderBindingName;
            cr.gridHeaderBindingFormat = this.gridHeaderBindingFormat;
        });

        this.grid().rowHeaders.columns[0].align = this.gridHeaderBindingAlign ?? 'left';
        this.grid().refresh(true);
        setTimeout(() => this.grid().autoSizeColumn(0, true), 100);
    }

    onGridMenuClick(e: MouseEvent) {
        this.gridMenu.show(e);
    }

    /**
     * Constructs a filter function that includes the custom error level filter
     * and the default filters of all other active filters.
     *
     ** (see https://www.grapecity.com/forums/wijmo/custom-filter-for-keyword-)
     */
    initCustomFilterFunction(grid: FlexGrid, filter: FlexGridFilter) {
        const activeFilters = range(grid.columns.length)
            .map(idx =>  filter.getColumnFilter(idx))
            .filter(filter => filter?.column.binding !== 'errorLevel')
            .filter(filter => filter?.isActive ?? false);

        const errorLevelFilter = filter.getColumnFilter('errorLevel');
        if (errorLevelFilter?.isActive) activeFilters.push(this.errorLevelFilterFun(errorLevelFilter));

        return (item: AccountData) => {
            return activeFilters.every(filter => filter.apply(item));
        };
    }

    /**
     * Filters account data based on specific error level criteria and selected filter values.
     * This is the implementation of the grid's custom filter function for the column 'errorLevel'.
     */
    private errorLevelFilterFun(filter: ColumnFilter): any {
        const values = filter.valueFilter.showValues;
        const showNoErrorsItems = !!values[NO_ERROR_FILTER_VALUE];
        return {
            apply: (item: AccountDataWithChecks) => {
                const checks = CheckUtils.getAccountChecks(item);
                const hasNoErrors = item.errorLevel === CheckSeverity.ok || item.errorLevel == null;
                return (showNoErrorsItems && hasNoErrors) || checks.some(c => values[c.id]);
            }
        }
    }

    async showCellCommentsEditor(item: AccountDataWithChecks, binding: string, cell: HTMLElement): Promise<void> {
        await this.gridCellRenderer?.showCommentsTooltipHandler(item, binding, cell, true);
    }

    clearGridFilters() {
        WjGridUtils.clearGridFilter(this.gridFilter(), false);
        this.gridSearch()!.text = '';
        this.applyPeriodFilter(this.pfSegData.periodFilter());
    }

    clearGridSorting() {
        const view = this.grid().collectionView;
        view.sortDescriptions.clear();
    }

    adjustColumnGroupVisibilities() {
        this.adjustColumnGroupVisibilitiesForGroup('TieringGroup', this.colVisibleTiering());
        this.adjustColumnGroupVisibilitiesForGroup('TrackingGroup', this.colVisibleTracking());
        this.adjustColumnGroupVisibilitiesForGroup('OutputGroup', this.colVisibleOutput());
        AppGridViewService.storeAccountColumnGroupState('TieringGroup', this.colVisibleTiering());
        AppGridViewService.storeAccountColumnGroupState('TrackingGroup', this.colVisibleTracking());
        AppGridViewService.storeAccountColumnGroupState('OutputGroup', this.colVisibleOutput());
    }

    adjustColumnGroupVisibilitiesForGroup(name: string, visible: boolean) {
        const columnGroup = this.findColumnGroupByName(this.grid(), name);
        if (columnGroup) {
            columnGroup.isCollapsed = !visible;
        }
    }

    private findColumnGroupByName(grid: FlexGrid | undefined, name: string): ColumnGroup | undefined {
        try {
            const columnGroups = grid?.getColumnGroups();
            return columnGroups?.find(g => g.name === name);
        } catch (error) {
            // this happens quite often when the grid has been disposed
            // but the polling handler is still running (e.g. when the grid is not visible)
            // cancel all...
            return undefined;
        }
    }

    private addPollingColumnGroupCollapseStatusChecker(grid: WjFlexGrid) {
        const statusChecker= IntervalWorker.schedule(`group-collapse-checker-${this.pfSegData.segmentId()}`, 1000, () => {
            if (!grid.hostElement) {
                console.warn('Grid host element does no longer exist - possibly after Angular hot reload. Cancelling the IntervalWorker');
                statusChecker.stop();
            }
            Stdlib.let(this.findColumnGroupByName(grid, 'TieringGroup'), g => this.colVisibleTiering.set(!g.isCollapsed));
            Stdlib.let(this.findColumnGroupByName(grid, 'TrackingGroup'), g => this.colVisibleTracking.set(!g.isCollapsed));
            Stdlib.let(this.findColumnGroupByName(grid, 'OutputGroup'), g => this.colVisibleOutput.set(!g.isCollapsed));

            this.gridFrozenColumnsActive.set(grid.frozenColumns > 0);
        });
        this.columnGroupCollapseStatusChecker = statusChecker;
    }

    onGridVisibilityChange(event: { element: HTMLElement; isVisible: boolean }) {
        if (event.isVisible) {
            this?.columnGroupCollapseStatusChecker?.start(false);
        } else {
            this?.columnGroupCollapseStatusChecker?.stop();
        }
    }

    private async onGridDataChanged(args: CellRangeEventArgs | undefined) {
        this.pfSegData.updateFormulasAndValidations();
    }

    private async openSharedViewIfNeeded(grid: WjFlexGrid, gridFilter: WjFlexGridFilter) {
        if (this.viewShareId()) {
            console.log(`Opening shared view with ID: ${this.viewShareId()}`);
            const id = this.viewShareId()!;
            await this.router.navigate([], { queryParams: { 'viewShareId': null }, queryParamsHandling: 'merge', replaceUrl: true });
            const layoutState = await this.keyValueAndParamsService.loadDataRecord(id) as GridLayoutState;
            if (!layoutState) {
                console.warn('No layout state found in shared view config');
            } else {
                await AppGridViewService.restoreLayout(grid, gridFilter, layoutState);
            }
        }
    }

    private monitorAccountChanges() {
        const changeList = this.pfSegData.accountsChangedInDbSinceLastLoad.value();
        if (changeList.length > 0) {
            console.log('Accounts changed in DB since last load');
            this.hasServerSideChanges.set(true);
            this.serverSideAccountChanges = new Map(changeList.map(i => [i.accountId, i]));
            this.grid()?.refresh(true);
        }
    }

    protected async onGridDownload() {
        const segment = this.pfSegData.activeSegment.value()!;

        // to export even the hidden columns, we save it first, then unhide them in the workbook
        // see https://developer.mescius.com/forums/wijmo/export-hidden-columns
        const wb = FlexGridXlsxConverter.saveAsync(this.grid(), {
            includeColumnHeaders: true,
                includeStyles: true,
                sheetName: `${segment.lobName} - ${segment.officeName}`,
                formatItem: (item: XlsxFormatItemEventArgs) => {
                    if (item.xlsxCell.formula) {
                        item.xlsxCell.value = item.xlsxCell.formula;
                        item.xlsxCell.formula = undefined;
                    }
                }
        });
        wb.sheets[0].columns.forEach(c => c.visible = true);
        wb.saveAsync(`${segment.lobName} - ${segment.officeName} - Accounts.xlsx`, (saved) => {
            // processing after saving - nothing to do
            console.debug(`workbook exported`);
        });
    }

    protected async syncWithServerData() {
        const btn = await Alerts.alertPopup({
            body: `This will merge the server-side account updates into your local data. Local changes will be overwritten.`,
            buttons: [DialogButton.Cancel, DialogButton.Ok],
        });
        if (btn === DialogButton.Ok) {
            await this.pfSegData.mergeServerSideAccountUpdates();
            this.hasServerSideChanges.set(false);
            this.serverSideAccountChanges.clear();
        }
    }

    toggleTableColumnLock(lock?: boolean) {
        const cursorColumnPosition = this.grid().selection.col ?? 0;
        this.grid().frozenColumns = this.gridFrozenColumnsActive() ? 0 : cursorColumnPosition;
    }

    onGridClick(grid: WjFlexGrid, event: PointerEvent) {
        const hti = grid.hitTest(event);
        if (hti.panel === grid.columnHeaders) {
            const row = hti.row;
            if (row === 2) {
                const column = hti.panel.columns[hti.col];
                const header = column.header;
                if (event.altKey && event.ctrlKey) {
                    column.visible = !column.visible;
                }
                console.log(`Clicked on (${row}, ${(hti.col)}) = ${header}`)
            }
        }
    }

    protected async onFetchFromAgora() {
        const selectedItems = this.grid().selectedItems as AccountData[];
        const selectedIds = selectedItems
            .filter(item => item.status === AccountDataStatusEnum.Open)
            .map(item => StringUtils.joinNotBlank(',', item.crmRenewalId, item.crmSubmissionId))
            .filter(id => id);

        await AgoraFetchDialogComponent.show(this.ngDynCompSvc, this.pfData.portfolio.value().id!, selectedIds);
    }

    protected toggleCommentsPane() {
        const newVisible = !this.commentPaneVisible();
        AppSessionStorage.setBooleanItem(AppConstants.VIEWSETTINGS_ACCOUNTGRID_COMMENTPANE_VISIBLE, newVisible);
        this.commentPaneVisible.set(newVisible);
    }

    protected onCellPaneCommentClick(comment: AccountFieldComments) {
        const grid = this.grid();
        const commentField = comment.attribute;
        const row = grid.rows.find((r) => r.dataItem.id === comment.account.id);
        const columnIndex = grid.columns.findIndex(c => c.binding === commentField);
        if (row) {
            grid.select(new CellRange(row.index, columnIndex));
            grid.focus();
        }
    }

    protected applyPeriodFilter(p: PortfolioPeriodEnum | undefined | null) {
        const gridFilter = this.gridFilter();
        if (!gridFilter) return;

        const countFilter = gridFilter.getColumnFilter('expiryDate');
        if (!countFilter) return;

        const year = this.pfSegData.portfolio.value()?.uwyear ?? new Date().getFullYear();
        if (!p) {
            countFilter.clear();
        } else {
            // the Wijmo date filter can only use '>' and not '>='
            // hence we use the day before our threshold date as start day
            const filterDates = AppConstants.ThresholdDates[p];
            const lowerDate = new Date(year, filterDates.lower.month, filterDates.lower.day - 1);

            countFilter.conditionFilter.condition1.operator = Operator.GT;
            countFilter.conditionFilter.condition1.value = lowerDate;
            countFilter.conditionFilter.condition2.operator = Operator.LT;
            countFilter.conditionFilter.condition2.value = new Date(year, filterDates.upper.month, filterDates.upper.day);
        }
        gridFilter.apply();
    }

    private prepareFilterTooltip(gridFilter: WjFlexGridFilter) {
        const filters = (gridFilter as any)._filters as ColumnFilter[];
        const filteredCols = filters.filter(f => f.isActive);

        const filteredAttrs = filteredCols.map(column  => {
            const attr = DataDictionaryService.getAccountDataAttribute(column.column.binding);
            if (!attr) return undefined;

            const attrBold = `<b>${attr.name}</b>`;
            const format = attr.format ?? '';
            if (column.valueFilter.isActive) {
                const values = Object.keys(column.valueFilter.showValues);
                const valuesAsText = values.map(v => v === '' ? '<em>none</em>' :Globalize.format(v, format));
                return `<span class="filter-values">${attrBold}: ${valuesAsText.join(', ')}</span>`
            } else if (column.conditionFilter.isActive) {
                const value1 = column.conditionFilter.condition1.value;
                const value2 = column.conditionFilter.condition2.value;
                return `${attrBold}: ${Globalize.format(value1, format)} < ... <=${Globalize.format(value2, format)}`;
            } else {
                return undefined;
            }
        }).sort();

        const listItems = filteredAttrs.filter(it => it);
        if (listItems.length === 0) {
            this.gridFilterTooltip.set('<em>No filter is active</em>');
        } else {
            const activeFiltersHtml = `<ul><li>${listItems.join('</li><li>')}</li></ul>`;

            const value = `
               <div class="content">
                    Active filters:
                    ${activeFiltersHtml}
               </div>
            `;
            this.gridFilterTooltip.set(value);
        }
    }
}
